import {
  Box,
  Button,
  HStack,
  Heading,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { usePublicSettings } from "../hooks/usePublicSettings"
import type { PublicClassFilters } from "../types"

interface ClassFiltersProps {
  filters: PublicClassFilters
  onFiltersChange: (filters: PublicClassFilters) => void
}

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
]

const TIME_OF_DAY_OPTIONS = [
  { value: "", label: "Any Time" },
  { value: "morning", label: "Morning (before 12pm)" },
  { value: "afternoon", label: "Afternoon (12pm-5pm)" },
  { value: "evening", label: "Evening (after 5pm)" },
] as const

const SORT_OPTIONS = [
  { value: "day_time", label: "Schedule" },
  { value: "price", label: "Price (Low to High)" },
  { value: "availability", label: "Most Available" },
] as const

const selectStyle = {
  width: "100%",
  padding: "8px",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "#E2E8F0",
  borderRadius: "6px",
  backgroundColor: "white",
  fontSize: "14px",
  cursor: "pointer",
  outline: "none",
} as const

export function ClassFilters({ filters, onFiltersChange }: ClassFiltersProps) {
  const { swimLevels, locations } = usePublicSettings()

  const handleDayChange = (day: string) => {
    const newDay = filters.day_of_week === day ? undefined : day
    onFiltersChange({ ...filters, day_of_week: newDay })
  }

  const handleTimeChange = (time: string) => {
    const timeValue = time as PublicClassFilters["time_of_day"] | ""
    onFiltersChange({
      ...filters,
      time_of_day: timeValue || undefined,
    })
  }

  const handleLevelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({
      ...filters,
      level: event.target.value || undefined,
    })
  }

  const handleLocationChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onFiltersChange({
      ...filters,
      location: event.target.value || undefined,
    })
  }

  const handleSortChange = (value: string) => {
    onFiltersChange({
      ...filters,
      sort_by: value as PublicClassFilters["sort_by"],
      offset: 0,
    })
  }

  const handleAvailableOnlyChange = () => {
    onFiltersChange({
      ...filters,
      available_only: !filters.available_only,
    })
  }

  const handleClearFilters = () => {
    onFiltersChange({
      session_id: filters.session_id,
      sort_by: filters.sort_by,
      limit: filters.limit,
      offset: 0,
    })
  }

  const hasActiveFilters = !!(
    filters.day_of_week ||
    filters.time_of_day ||
    filters.level ||
    filters.location ||
    filters.available_only
  )

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      p={5}
    >
      <VStack align="stretch" gap={5}>
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Heading size="md">Filters</Heading>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              colorPalette="blue"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          )}
        </HStack>

        {/* Sort */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            Sort By
          </Text>
          <Stack gap={1}>
            {SORT_OPTIONS.map((option) => (
              <Box
                key={option.value}
                as="label"
                display="flex"
                alignItems="center"
                gap={2}
                cursor="pointer"
                py={1}
                _hover={{ bg: "gray.50" }}
                borderRadius="md"
                px={2}
              >
                <input
                  type="radio"
                  name="sort_by"
                  value={option.value}
                  checked={(filters.sort_by || "day_time") === option.value}
                  onChange={() => handleSortChange(option.value)}
                  style={{
                    width: "16px",
                    height: "16px",
                    accentColor: "#3182ce",
                    cursor: "pointer",
                  }}
                />
                <Text fontSize="sm">{option.label}</Text>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Day of Week Filter */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            Day of Week
          </Text>
          <Stack gap={1}>
            {DAYS_OF_WEEK.map((day) => (
              <Box
                key={day}
                as="label"
                display="flex"
                alignItems="center"
                gap={2}
                cursor="pointer"
                py={1}
                _hover={{ bg: "gray.50" }}
                borderRadius="md"
                px={2}
              >
                <input
                  type="checkbox"
                  checked={filters.day_of_week === day}
                  onChange={() => handleDayChange(day)}
                  style={{
                    width: "16px",
                    height: "16px",
                    accentColor: "#3182ce",
                    cursor: "pointer",
                  }}
                />
                <Text fontSize="sm">{day}</Text>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Time of Day Filter */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            Time of Day
          </Text>
          <Stack gap={1}>
            {TIME_OF_DAY_OPTIONS.map((option) => (
              <Box
                key={option.value || "any"}
                as="label"
                display="flex"
                alignItems="center"
                gap={2}
                cursor="pointer"
                py={1}
                _hover={{ bg: "gray.50" }}
                borderRadius="md"
                px={2}
              >
                <input
                  type="radio"
                  name="time_of_day"
                  value={option.value}
                  checked={(filters.time_of_day || "") === option.value}
                  onChange={() => handleTimeChange(option.value)}
                  style={{
                    width: "16px",
                    height: "16px",
                    accentColor: "#3182ce",
                    cursor: "pointer",
                  }}
                />
                <Text fontSize="sm">{option.label}</Text>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Swim Level Filter */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            Swim Level
          </Text>
          <select
            value={filters.level || ""}
            onChange={handleLevelChange}
            style={selectStyle}
          >
            <option value="">All Levels</option>
            {swimLevels.map((sl) => (
              <option key={sl.value} value={sl.value}>
                {sl.label}
              </option>
            ))}
          </select>
        </Box>

        {/* Location Filter */}
        {locations.length > 0 && (
          <Box>
            <Text fontWeight="medium" mb={2}>
              Location
            </Text>
            <select
              value={filters.location || ""}
              onChange={handleLocationChange}
              style={selectStyle}
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.value} value={loc.value}>
                  {loc.label}
                </option>
              ))}
            </select>
          </Box>
        )}

        {/* Availability Filter */}
        <Box>
          <Box
            as="label"
            display="flex"
            alignItems="center"
            gap={2}
            cursor="pointer"
            py={1}
            _hover={{ bg: "gray.50" }}
            borderRadius="md"
            px={2}
          >
            <input
              type="checkbox"
              checked={!!filters.available_only}
              onChange={handleAvailableOnlyChange}
              style={{
                width: "16px",
                height: "16px",
                accentColor: "#3182ce",
                cursor: "pointer",
              }}
            />
            <Text fontWeight="medium" fontSize="sm">
              Available classes only
            </Text>
          </Box>
        </Box>

        {/* Clear Filters Button (shown at bottom when filters active) */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            colorPalette="gray"
            width="full"
            onClick={handleClearFilters}
          >
            Clear All Filters
          </Button>
        )}
      </VStack>
    </Box>
  )
}
